import React = require('react');
import { Messages } from '../../../../../codegen/Localize';
import { L10n } from '../../../../../commonInfra/L10n/L10n';
import { LicenseInfoBar } from '../../../../../license/components/header/LicenseInfoBar';
import { IProductKeyInfo } from '../../../../../license/serverApi/types/ILicensingInfo';
import { LicenseTabStyle } from '../../../../../license/styles/LicenseTabStyle';
import { getSessionManagementServiceInstance } from '../../../../../sessionManagement/SessionManagementService';
import { ILicensingInfoWithTotals } from '../../../../../userManagement/services/licenses/LicensesService';

export interface LicenseTabComponentProps {
  readonly licensingInfo: ILicensingInfoWithTotals;
}

export function renderLicenseTabComponent(licensingInfo: ILicensingInfoWithTotals): JSX.Element {
  const isEmpty = ! licensingInfo.productKeyInfos || ! licensingInfo.productKeyInfos.length;
  const shouldShowSubscriptionLicenseBar = getSessionManagementServiceInstance().shouldShowServerLicensingTierInfo();
  const hasAnyCoreLicense = licensingInfo.totalCoreLicenses > 0 ||  licensingInfo.totalPrepCoreLicenses > 0;
  return (
    <div className="tb-tabs-banner-subplace">
      {isEmpty &&
        <div className="tb-fill">
          <div className="tb-data-grid-empty">
            <div className="tb-data-grid-empty-title" data-tb-test-id='empty-license-message'>
              {Messages.licensesEmptyMessage()}
            </div>
          </div>
        </div>
      }
      {
        !isEmpty &&
        <div className="tb-scroll tb-scroll-settings">
          <div className="tb-settings-info-container tb-settings-section">
            {
             shouldShowSubscriptionLicenseBar && createLicensingInfoBar()
            }
          </div>
          <div className="tb-settings-separator"></div>
          <div className={LicenseTabStyle.settingContainer}>
              <div className="tb-settings-section">
                <div className="tb-settings-group-name" data-tb-test-id='product-key-message'>
                    {Messages.productKeys()}
                </div>
                {createProductKeyTable(licensingInfo)}
              </div>
          </div>
          {
              hasAnyCoreLicense &&
              <div className={LicenseTabStyle.settingContainer}>
                <div className="tb-settings-section">
                  <div className="tb-settings-group-name" data-tb-test-id='core-license-message'>
                    {Messages.coreLicenses()}
                  </div>
                  {createCoreLicenseTable(licensingInfo)}
                </div>
              </div>
          }
          {
            hasAnyCoreLicense &&
            <div className="tb-settings-separator"></div>
          }
          {
            hasAnyCoreLicense &&
            <div className={LicenseTabStyle.settingContainer}>
              <div className="tb-settings-section">
                <div className="tb-settings-group-name" data-tb-test-id='core-license-Assignment-message'>
                  {Messages.coreLicenseAssignment()}
                </div>
                {createCoreLicenseAssignmentTable(licensingInfo)}
              </div>
            </div>
          }
        </div>
      }
    </div>
  );

}

function createProductKeyTable(licensingInfo: ILicensingInfoWithTotals) {
  const productKeyInfos = licensingInfo.productKeyInfos;
  return (
    <>
      <table className="tb-static-grid-table tb-static-grid-table-product-key-width" data-tb-test-id="productKeyInfo-table">
        <tbody>
        <tr>
          {createLicenseHeaderCell(Messages.productKey(), false, false)}
          {createLicenseHeaderCellMultiline(Messages.coreCountRegular(), true, true)}
          {createLicenseHeaderCellMultiline(Messages.coreCountPrep(), true, true)}
          {createLicenseHeaderCell(Messages.license_CREATOR(), true, true)}
          {createLicenseHeaderCell(Messages.license_EXPLORER(), true, true)}
          {createLicenseHeaderCell(Messages.license_VIEWER(), true, true)}
          {createLicenseHeaderCell(Messages.guest(), false, true)}
          {createLicenseHeaderCell(Messages.valid(), false, true)}
          {createLicenseHeaderCellMultiline(Messages.license_itMgtBundle(), false, true)}
          {createLicenseHeaderCellMultiline(Messages.license_dataMgtBundle(), false, true)}
          {createLicenseHeaderCellMultiline(Messages.licenseSubscriptionEnds(), false, false)}
          {createLicenseHeaderCellMultiline(Messages.maintenanceExpires(), false, false)}
        </tr>
        {
          productKeyInfos.map((productKeyInfo, index) => {
            return (
              <tr key={index} data-tb-test-id="productKeyInfo-list">
                {createBodyCell(productKeyInfo.serial, false)}
                {createBodyCellCountRegular(productKeyInfo)}
                {createBodyCellCountPrep(productKeyInfo)}
                {createBodyCell(Number(productKeyInfo.creatorUserCount), true)}
                {createBodyCell(getCountOrUnlimited(productKeyInfo.numCores, productKeyInfo.explorerUserCount), true)}
                {createBodyCell(getCountOrUnlimited(productKeyInfo.numCores, productKeyInfo.viewerUserCount), true)}
                {createBodyCellBoolean(productKeyInfo.isGuestAllowed)}
                {createBodyCellBoolean(productKeyInfo.isValid)}
                {createBodyCellBoolean(productKeyInfo.isITManagementBundleEnabled)}
                {createBodyCellBoolean(productKeyInfo.isPrepEnabled)}
                {createBodyCellDate(productKeyInfo.expiration)}
                {createBodyCellMaintenanceDate(productKeyInfo)}
              </tr>
            );
          })
        }
        </tbody>
      </table>
    </>
  );
}

function createCoreLicenseTable(licensingInfo: ILicensingInfoWithTotals) {
  return (
    <table className="tb-static-grid-table tb-static-grid-table-settings-max-width" data-tb-test-id="coreLicense-table">
      <tr>
        {createLicenseHeaderCell(Messages.product(), false, false)}
        {createLicenseHeaderCell(Messages.totalCores(), false, false)}
        {createLicenseHeaderCell(Messages.inUse(), false, false)}
        {createLicenseHeaderCell(Messages.available(), false, false)}
      </tr>
      <tr>
        {createBodyCellLabel(Messages.tableauServer())}
        {createBodyCell(licensingInfo.totalCoreLicenses, true)}
        {createBodyCell(licensingInfo.totalLicensedRegularCores, true)}
        {createBodyCell(licensingInfo.totalRegularCoreLicensesAvailable, true)}
      </tr>
      <tr>
        {createBodyCellLabel(Messages.flowprocessor())}
        {createBodyCell(licensingInfo.totalPrepCoreLicenses, true)}
        {createBodyCell(licensingInfo.totalLicensedPrepCores, true)}
        {createBodyCell(licensingInfo.totalPrepCoreLicensesAvailable, true)}
      </tr>
    </table>
  );

}

function createCoreLicenseAssignmentTable(licensingInfo: ILicensingInfoWithTotals) {
  const coreLicensingInfos = licensingInfo.coreLicensingInfos;
  return (
    <>
      <table className="tb-static-grid-table tb-static-grid-table-settings-width" data-tb-test-id="coreLicenseAssignment-table">
        <tr>
          {createLicenseHeaderCell(Messages.machine(), false, false)}
          {createLicenseHeaderCell(Messages.cores(), true, true)}
          {createLicenseHeaderCell(Messages.coreCountRegular(), true, true)}
          {createLicenseHeaderCell(Messages.coreCountPrep(), true, true)}
        </tr>
        {
          coreLicensingInfos.map((coreLicensingInfo, index) => {
            return (
              <tr key={index} data-tb-test-id="coreLicense-list">
                {createBodyCell(coreLicensingInfo.machineId, false)}
                {createBodyCell(coreLicensingInfo.numMachineCores, true)}
                {createBodyCell(coreLicensingInfo.numLicensedCores, true)}
                {createBodyCell(coreLicensingInfo.numLicensedPrepCores, true)}
              </tr>
            );
          })
        }
      </table>
    </>
  );
}

function getCountOrUnlimited(numCores: number, userCount: number) {
  if(numCores){
    return Messages.unlimited();
  }
  return userCount;
}

function createLicenseHeaderCell(content: string, isNumber: boolean, hasExtraClass: boolean) {
  return (
      <th className={getHeaderClassNames(isNumber, hasExtraClass)}>
        <span className="tb-data-grid-header-text">{content}</span>
      </th>
  );
}

function createLicenseHeaderCellMultiline(content: string, isNumber: boolean, hasExtraClass: boolean) {
  return (
    <th className={getHeaderClassNames(isNumber, hasExtraClass)}>
      <span className="tb-data-grid-licenses-header-text-multiline">{content}</span>
    </th>
  );
}

function createBodyCell(content: number | string, hasExtraClass: boolean) {
  return (
      <td className={getBodyClassNames(hasExtraClass)}>
        <span className="tb-data-grid-cell-text tb-lr-padded-wide">{content}</span>
      </td>
  );
}

function createBodyCellLabel(content: string) {
  return (
    <td className={getBodyClassNames(false)}>
      <span className="tb-data-grid-cell-text tb-lr-padded-wide">{content}</span>
    </td>
  );
}

function createBodyCellBoolean(value: boolean) {
  return (
    <td className="tb-data-grid-separator-row tb-data-grid-cell-text-icon tb-lr-padded-wide">
      {value ? React.createElement('span', { className: 'tb-data-grid-cell-text tb-data-grid-icon tb-icon-checkmark' }) : null}
    </td>
  );
}

function createBodyCellDate(date: string) {
  return (
    <td className="tb-data-grid-separator-row tb-data-grid-cell-text-date">
      <span className="tb-data-grid-cell-text tb-lr-padded-wide-right">
        {formatExpirationDate(date)}
      </span>
    </td>
  );
}

function createBodyCellCountRegular(productKeyInfo: IProductKeyInfo) {
  return (
    <td className={getBodyClassNames(true)}>
      <span className="tb-data-grid-cell-text tb-lr-padded-wide">
        {productKeyInfo.numCores}
      </span>
    </td>
  );
}

function createBodyCellCountPrep(productKeyInfo: IProductKeyInfo) {
  return (
    <td className={getBodyClassNames(true)}>
      <span className="tb-data-grid-cell-text tb-lr-padded-wide">
        {productKeyInfo.numPrepCores}
      </span>
    </td>
  );
}

function createBodyCellMaintenanceDate(productKeyInfo: IProductKeyInfo) {
  return (
    <td className="tb-data-grid-separator-row tb-data-grid-cell-text-date">
      <span className="tb-data-grid-cell-text tb-lr-padded-wide-right">
        {maintenanceDate(productKeyInfo)}
      </span>
    </td>
  );
}

function createLicensingInfoBar() {
  return <LicenseInfoBar
    hasSiteLicensingInfo={false}/>;
}

const PermanentRegExp = /permanent/i;
const TrialRegExp = /trial/i;

function formatExpirationDate(date: string) {
  if (!date || TrialRegExp.test(date)) {
    return '';
  } else if (PermanentRegExp.test(date)) {
    return Messages.license_expiration_permanent();
  } else {
    return L10n.localizedDate(date);
  }
}

function maintenanceDate(productKeyInfo: IProductKeyInfo) {
  if(PermanentRegExp.test(productKeyInfo.expiration)){
    return L10n.localizedDate(productKeyInfo.maintenance);
  } else {
    return Messages.notApplicable();
  }
}

function getHeaderClassNames(isNumber: boolean, hasExtraClass: boolean) {
  const classNames = [LicenseTabStyle.headerLine];

  if(!hasExtraClass) {
    return classNames.join(' ');
  } else {
    isNumber? classNames.push(LicenseTabStyle.headerNumberic) :
    classNames.push(LicenseTabStyle.headerIcon);
  }

  return classNames.join(' ');
}

function getBodyClassNames(hasExtraClass: boolean) {
  const classNames = ['tb-data-grid-separator-row'];
  if(hasExtraClass) {
    classNames.push(LicenseTabStyle.headerNumberic);
  }
  return classNames.join(' ');
}
